
import {useState} from 'external/react'
import {styled, clsx} from 'external/material'
import {formatDate} from 'external/date-fns'

import { inViewer } from 'settings'

import {isEmpty} from 'utils/text'


import {MessageIcon} from 'resources/icons'

import useTranslations from 'hooks/translations'
import useCatalog from 'hooks/catalog'

import DashboardActivityItemType from './DashboardActivityItemType'
import DashboardActivityItemLink from './DashboardActivityItemLink'
import DashboardActivityItemDownload from './DashboardActivityItemDownload'

import Teacher from 'components/Teacher'
import Message from 'components/Message'


import { dashboardActivityItemWidth, dashboardActivityItemHeight, dashboardActivityItemMargin } from 'settings'
import Group from 'components/Group'
import { centerVertically } from 'utils/style'

import MultilineText from 'components/MultilineText'

const StyledDashboardActivityItem = styled('div')(dashboardActivityItemStyles)

export default function DashboardActivityItem(props) {

    const [[openMessage, anchorEl], setOpenMessage] = useState([false, null])
    const translations = useTranslations()
    const {isDownloaded} = useCatalog()
    const {translate} = translations
    const {activity, group = {}} = props
    const downloaded = isDownloaded({id: activity.title, hash: activity.hash})
    const {name, content = {}, hasEpl} = activity

    const lesson = activity.hasEpl ? activity.title : undefined


   const hasTitle = activity.type != 'comment' 
   const messageText =  activity.type == 'assignment'
    ? content.comments
    : activity.type == 'homework'
        ? content.instructions
        : content.message

    const title = ['assignment', 'homework'].indexOf(activity.type) >= 0
        ? activity.title
        : activity.hasEpl
            ? name && name.replace(`${lesson}: `, '')
            : messageText

   const message = !isEmpty(messageText) && {
        hasTitle,
        date: translations.formatDate(activity.modified),
        lesson: activity.hasEpl ? lesson : translate(`${activity.type}-activity-label`), title, text: messageText
    }

    return(<StyledDashboardActivityItem>
        
            <div className='topRow'>
                <DashboardActivityItemType {...activity} />
                <div className='dates'>
                    <div className='month-year'>{formatDate(activity.modified, 'MMM dd, yyyy', translations.language)}</div>
                </div>
            </div>
            <div className='titleRow'>
                <div className='lesson'>
                    <div className='code'>{lesson}</div>
                </div>
                <MultilineText className='title' fontSize='10.5pt' width={228} text={title} maxLines={lesson ? 1 : 2} />
            </div>
            <div className='teacherRow'>
                <Teacher {...group} />
            </div>
            <div className='groupRow'>
                <Group showLabel name={group.name} fill="#639fe4" width={30} />
            </div>
            <div className='assignedDateRow'>
                <div className='assignedDateColumn'>
                    <div className='assignedDateLabel'>{translate('assigned-to-date')}:</div>
                    <div className='assignedDateText'>{formatDate(activity.start, 'dd/MM/yyyy', translations.language)}</div>
                </div>
                <div className='messageColumn'>
                    <div onClick={e => !isEmpty(messageText) && setOpenMessage([true, e.target])} className={clsx('messageButton', {noMessage: isEmpty(messageText)})}>
                        <div className='iconContainer'>
                            <MessageIcon fill="#639fe4" width={18} />
                        </div>
                    </div>
                    <Message open={openMessage} anchorEl={anchorEl} {...message} onClose={() => setOpenMessage([false, null])} />
                </div>
            </div>

            <div style={{flexGrow: 1}} />
            <div className={clsx('linkRow', {hidden: activity.type == 'comment'})}>
                {!inViewer && (<DashboardActivityItemLink  {...activity} lesson={lesson} />)}
                {inViewer && !hasEpl && (<DashboardActivityItemLink  {...activity} lesson={lesson} />)}
                {inViewer && hasEpl && (
                    downloaded
                    ? (<DashboardActivityItemDownload  downloaded {...activity} lesson={lesson} />)
                    : (<div className="link-row-container">
                        <DashboardActivityItemLink  {...activity} lesson={lesson} />
                        <DashboardActivityItemDownload  downloaded={false} {...activity} lesson={lesson} />
                    </div>
                    )
                )}
            </div>

        </StyledDashboardActivityItem>
   )
}

function dashboardActivityItemStyles(){
    return {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        flexShrink: 0,
        width: dashboardActivityItemWidth,
        height: dashboardActivityItemHeight,
        marginLeft: dashboardActivityItemMargin,
        "&:first-of-type": {
            marginLeft: 0
        },
        padding: 20,
        borderRadius: 10,
        marginTop: 20,
        color: '#798092',
        border: 'solid 1px #e5e7ee',
        boxShadow: '1px 0px 4px #e5e7ee',
        "&:hover": {
            boxShadow: '-4px 0px 15px #e5e7ee',
        },
        "& > div": {
            flexShrink: 0
        },
        '& > .topRow': {
            display: 'flex',
            '& .dates': {
                textAlign: 'right',
                marginLeft: 10,
                flexGrow: 1,
                ...centerVertically,
                '& .month-year': {
                    color: '#313c5b',
                    fontSize: '10.5pt',
                    textTransform: 'capitalize'
                }
            },
            '& .teacher': {
                '& .MuiAvatar-root': {
                    height: 60,
                    width: 60,
                }
            }
        },
        '& > .teacherRow': {
            marginTop: 15,
            marginBottom: 15,
            '& .teacher': {
                display: 'flex',
            },
            '& .teacherName': {
                flexGrow: 1,
                textOverflow: 'ellipsis',
                marginLeft: 10,
                color: '#313c5b',
                ...centerVertically,
                fontSize: '10.5pt'
            }
        },
        '& > .titleRow': {
            fontSize: '10.5pt',
            height: 50,
            flexShrink: 0,
            flexGrow: 1,
            marginTop: 15,
            '& .lesson': {
                fontWeight: 500,
                display: 'flex',
                '& .code': {
                    flexGrow: 1
                }
            },
            '& .title': {
                marginTop: 4,
            }
        },
        '& .groupRow': {
            '& .group': {
                display: 'flex',
                flexWrap: 'wrap',
                '& .groupLabel': {
                    marginLeft: 2,
                    fontSize: '10.5pt',
                    width: '100%',
                    marginBottom: 10,
                    '&::after': {
                        content: "':'"
                    }
                },
                '& .groupIcon': {
                    marginLeft: 3,
                },
                '& .groupName': {
                    paddingTop: 2,
                    marginLeft: 8,
                    color: '#313c5b',
                    fontSize: '10.5pt',
                }
            }
        },
        '& .assignedDateRow': {
            marginTop: 20,
            display: 'flex',
            '& .assignedDateColumn': {
               ...centerVertically,
               flexGrow: 1,
                fontSize: '10.5pt',
                '& .assignedDateText': {
                    color: '#313c5b',
                    
                } 
            },
            '& .messageColumn': {
                ...centerVertically,
                '& .messageButton': {
                    flexGrow: 1,
                    ...centerVertically,
                    cursor: 'pointer',
                    '&.noMessage': {
                        cursor: 'default',
                        opacity: '0.6'
                    },
                    '& .iconContainer': {
                        display: 'flex',
                        justifyContent: 'center',

                    },
                    width: 65,
                    border: 'solid 1px #d4d4d4',
                    borderRadius: 10 
                }
            }
        },
        '& .linkRow': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            marginTop: 20,
            paddingBottom: 10,
            '&.hidden': {
                visibility: 'hidden'
            },
            '& .link-row-container': {
                display: 'flex',
                '& > div': {
                    flexShrink: 0,
                    '&:first-of-type': {
                        marginRight: 10
                    }
               }
            }
        }
    }
}
